document.addEventListener("DOMContentLoaded", function () {
  const options = {
    threshold: 0.3,
  };
  const isShopifyDesignMode = window.Shopify?.designMode === true;
  const animationItemSelector = "yg-animation-item, .yg-animation-item";
  const scrollRestoreStorageKeyPrefix = "yg:return-scroll:";
  const shopifyInstallmentsFontStyleId = "yg-shopify-installments-font";

  function applyShopifyInstallmentsFont(paymentTerms, attempt = 0) {
    if (!(paymentTerms instanceof HTMLElement)) return;

    const shadowRoot = paymentTerms.shadowRoot;
    if (!shadowRoot) {
      if (attempt < 20) {
        window.requestAnimationFrame(() => applyShopifyInstallmentsFont(paymentTerms, attempt + 1));
      }
      return;
    }

    if (shadowRoot.getElementById(shopifyInstallmentsFontStyleId)) return;

    const style = document.createElement("style");
    style.id = shopifyInstallmentsFontStyleId;
    style.textContent = "#shopify-installments { font-family: var(--font-body-family) !important; }";
    shadowRoot.append(style);
  }

  function initializeShopifyInstallmentsFont(root = document) {
    if (root instanceof Element && root.matches("shopify-payment-terms")) {
      applyShopifyInstallmentsFont(root);
    }

    root.querySelectorAll?.("shopify-payment-terms").forEach(applyShopifyInstallmentsFont);
  }

  initializeShopifyInstallmentsFont();

  customElements.whenDefined("shopify-payment-terms").then(() => {
    initializeShopifyInstallmentsFont();
  });

  const shopifyInstallmentsObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) initializeShopifyInstallmentsFont(node);
      });
    });
  });

  shopifyInstallmentsObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });

  class YGProductSummary extends HTMLElement {
    connectedCallback() {
      if (this.isInitialized) return;
      this.isInitialized = true;
      this.updateFrame = null;
      this.queueUpdate = this.queueUpdate.bind(this);
      document.addEventListener('yg:variant-change', this.queueUpdate);
      document.addEventListener('yg:addons-change', this.queueUpdate);
      document.addEventListener('variant:change', this.queueUpdate);
      this.update();
    }

    disconnectedCallback() {
      document.removeEventListener('yg:variant-change', this.queueUpdate);
      document.removeEventListener('yg:addons-change', this.queueUpdate);
      document.removeEventListener('variant:change', this.queueUpdate);
      if (this.updateFrame !== null) window.cancelAnimationFrame(this.updateFrame);
      this.updateFrame = null;
      this.isInitialized = false;
    }

    queueUpdate() {
      if (this.updateFrame !== null) return;
      this.updateFrame = window.requestAnimationFrame(() => {
        this.updateFrame = null;
        if (this.isConnected) this.update();
      });
    }

    update() {
      const productRoot = document.querySelector('#MainContent product-info') || document.querySelector('product-info');
      const activeVariant = productRoot?.querySelector(
        '.yg-variant-kit-picker__cards [data-variant-card].is-active'
      );
      const productTitle = this.dataset.productTitle?.trim();
      const variantTitle = activeVariant?.dataset.variantTitle?.trim() || this.dataset.initialVariantTitle?.trim();
      const addonTitles = [];
      const seenAddonTitles = new Set();

      productRoot?.querySelectorAll('yg-product-addons [data-product-addon-item]').forEach((item) => {
        const quantity = Number.parseInt(item.dataset.quantity, 10) || 0;
        const addonTitle = item.dataset.productTitle?.trim();
        if (quantity <= 0 || !addonTitle || seenAddonTitles.has(addonTitle)) return;
        seenAddonTitles.add(addonTitle);
        addonTitles.push(addonTitle);
      });

      this.renderItems([productTitle, variantTitle, ...addonTitles].filter(Boolean));
    }

    renderItems(items) {
      const fragment = document.createDocumentFragment();

      items.forEach((item, index) => {
        if (index > 0) {
          const divider = document.createElement('span');
          divider.className = 'yg-product-summary__divider';
          divider.setAttribute('aria-hidden', 'true');
          fragment.append(divider);
        }

        fragment.append(document.createTextNode(item));
      });

      this.replaceChildren(fragment);
    }
  }

  if (!customElements.get('yg-product-summary')) {
    customElements.define('yg-product-summary', YGProductSummary);
  }

  class YGProductAddons extends HTMLElement {
    connectedCallback() {
      if (this.isInitialized) return;
      this.form = document.getElementById(this.dataset.productFormId);
      if (!this.form) return;

      this.isInitialized = true;
      this.isSubmitting = false;
      this.productForm = this.form.closest('product-form');
      this.cart =
        document.querySelector('cart-notification') ||
        document.querySelector('cart-drawer') ||
        document.querySelector('beni-cart-popover');
      this.list = this.querySelector('.product-addons__list');
      this.boundClickHandler = this.handleClick.bind(this);
      this.boundSubmitHandler = this.handleSubmit.bind(this);
      this.addEventListener('click', this.boundClickHandler);
      this.form.addEventListener('submit', this.boundSubmitHandler, true);
      this.querySelectorAll('[data-product-addon-item]').forEach((item) => this.updateItemQuantity(item, 0, false));
    }

    disconnectedCallback() {
      if (!this.isInitialized) return;
      this.removeEventListener('click', this.boundClickHandler);
      this.form?.removeEventListener('submit', this.boundSubmitHandler, true);
      this.isInitialized = false;
    }

    handleClick(event) {
      const toggle = event.target.closest('[data-product-addons-toggle]');
      if (toggle) {
        this.toggleItems(toggle);
        return;
      }

      const increaseButton = event.target.closest('[data-product-addon-increase]');
      const decreaseButton = event.target.closest('[data-product-addon-decrease]');
      const button = increaseButton || decreaseButton;
      if (!button || button.disabled) return;

      const item = button.closest('[data-product-addon-item]');
      if (!item) return;

      const currentQuantity = Number.parseInt(item.dataset.quantity, 10) || 0;
      const maxQuantity = Number.parseInt(item.dataset.maxQuantity, 10);
      if (increaseButton && Number.isFinite(maxQuantity) && currentQuantity >= maxQuantity) {
        this.setInventoryNotice(item, true);
        return;
      }

      if (decreaseButton) this.setInventoryNotice(item, false);
      const nextQuantity = increaseButton ? currentQuantity + 1 : Math.max(0, currentQuantity - 1);
      this.updateItemQuantity(item, nextQuantity);
    }

    toggleItems(toggle) {
      const nextExpanded = toggle.getAttribute('aria-expanded') !== 'true';
      const label = toggle.querySelector('[data-product-addons-toggle-label]');
      const items = Array.from(this.querySelectorAll('[data-product-addon-item]'));
      const extraItems = items.slice(3);

      if (!this.list || !extraItems.length) return;

      const startHeight = this.list.getBoundingClientRect().height;
      let endHeight = startHeight;

      this.list.getAnimations().forEach((animation) => animation.cancel());
      if (this.listTransitionEndHandler) {
        this.list.removeEventListener('transitionend', this.listTransitionEndHandler);
      }
      this.list.style.height = `${startHeight}px`;
      this.list.style.overflow = 'hidden';

      if (nextExpanded) {
        extraItems.forEach((item) => {
          item.hidden = false;
        });
        endHeight = this.list.scrollHeight;
      } else {
        const lastVisibleItem = items[2];
        const listTop = this.list.getBoundingClientRect().top;
        endHeight = lastVisibleItem ? lastVisibleItem.getBoundingClientRect().bottom - listTop : 0;
      }

      toggle.setAttribute('aria-expanded', String(nextExpanded));
      if (label) label.textContent = nextExpanded ? toggle.dataset.lessLabel : toggle.dataset.moreLabel;

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        if (!nextExpanded) extraItems.forEach((item) => (item.hidden = true));
        this.clearListAnimationStyles();
        return;
      }

      requestAnimationFrame(() => {
        this.list.style.height = `${endHeight}px`;
      });

      this.listTransitionEndHandler = (event) => {
        if (event.propertyName !== 'height') return;
        if (!nextExpanded) extraItems.forEach((item) => (item.hidden = true));
        this.list.removeEventListener('transitionend', this.listTransitionEndHandler);
        this.listTransitionEndHandler = null;
        this.clearListAnimationStyles();
      };
      this.list.addEventListener('transitionend', this.listTransitionEndHandler);
    }

    clearListAnimationStyles() {
      if (!this.list) return;
      this.list.style.height = '';
      this.list.style.overflow = '';
    }

    updateItemQuantity(item, quantity, notify = true) {
      const parsedMaxQuantity = Number.parseInt(item.dataset.maxQuantity, 10);
      const hasMaxQuantity = Number.isFinite(parsedMaxQuantity) && parsedMaxQuantity >= 0;
      const safeQuantity = hasMaxQuantity
        ? Math.min(parsedMaxQuantity, Math.max(0, quantity))
        : Math.max(0, quantity);
      const currentQuantity = Number.parseInt(item.dataset.quantity, 10) || 0;
      const quantityElement = item.querySelector('[data-product-addon-quantity]');
      const increaseButton = item.querySelector('[data-product-addon-increase]');
      const decreaseButton = item.querySelector('[data-product-addon-decrease]');

      item.dataset.quantity = String(safeQuantity);
      if (quantityElement) {
        quantityElement.textContent = `x${safeQuantity}`;
        quantityElement.dataset.hasQuantity = String(safeQuantity > 0);
      }
      if (increaseButton) increaseButton.disabled = hasMaxQuantity && safeQuantity >= parsedMaxQuantity;
      if (decreaseButton) decreaseButton.disabled = safeQuantity === 0;
      this.setInventoryNotice(item, hasMaxQuantity && safeQuantity >= parsedMaxQuantity);
      if (notify && safeQuantity !== currentQuantity) this.dispatchAddonsChange();
    }

    setInventoryNotice(item, visible) {
      const notice = item.querySelector('[data-product-addon-inventory-notice]');
      if (!notice) return;
      notice.hidden = !visible;
    }

    resetQuantities() {
      let changed = false;
      this.querySelectorAll('[data-product-addon-item]').forEach((item) => {
        const quantity = Number.parseInt(item.dataset.quantity, 10) || 0;
        if (quantity <= 0) return;
        this.updateItemQuantity(item, 0, false);
        changed = true;
      });
      if (changed) this.dispatchAddonsChange();
    }

    decrementRemainingInventory(addonLines) {
      const addedQuantities = new Map(addonLines.map((line) => [line.id, line.quantity]));
      let quantityChanged = false;

      this.querySelectorAll('[data-product-addon-item]').forEach((item) => {
        const variantId = Number.parseInt(item.dataset.variantId, 10);
        const addedQuantity = addedQuantities.get(variantId) || 0;
        const currentMaxQuantity = Number.parseInt(item.dataset.maxQuantity, 10);
        if (!addedQuantity || !Number.isFinite(currentMaxQuantity)) return;

        const currentQuantity = Number.parseInt(item.dataset.quantity, 10) || 0;
        item.dataset.maxQuantity = String(Math.max(0, currentMaxQuantity - addedQuantity));
        this.updateItemQuantity(item, currentQuantity, false);
        quantityChanged = quantityChanged || Number.parseInt(item.dataset.quantity, 10) !== currentQuantity;
      });

      if (quantityChanged) this.dispatchAddonsChange();
    }

    dispatchAddonsChange() {
      this.dispatchEvent(
        new CustomEvent('yg:addons-change', {
          bubbles: true,
          detail: { items: this.getAddonLines() },
        })
      );
    }

    getAddonLines() {
      const quantitiesByVariant = new Map();

      this.querySelectorAll('[data-product-addon-item]').forEach((item) => {
        const variantId = Number.parseInt(item.dataset.variantId, 10);
        const quantity = Number.parseInt(item.dataset.quantity, 10) || 0;
        if (!variantId || quantity <= 0) return;
        quantitiesByVariant.set(variantId, (quantitiesByVariant.get(variantId) || 0) + quantity);
      });

      return Array.from(quantitiesByVariant, ([id, quantity]) => ({ id, quantity }));
    }

    getMainProductLine(formData) {
      const variantId = Number.parseInt(formData.get('id'), 10);
      if (!variantId) return null;

      const line = {
        id: variantId,
        quantity: Math.max(1, Number.parseInt(formData.get('quantity'), 10) || 1),
      };
      const sellingPlan = formData.get('selling_plan');
      const properties = {};

      if (sellingPlan) line.selling_plan = Number.parseInt(sellingPlan, 10) || sellingPlan;
      formData.forEach((value, key) => {
        const propertyMatch = key.match(/^properties\[(.+)]$/);
        if (!propertyMatch || value === '') return;
        properties[propertyMatch[1]] = value;
      });
      if (Object.keys(properties).length) line.properties = properties;
      return line;
    }

    async handleSubmit(event) {
      const addonLines = this.getAddonLines();
      if (!addonLines.length) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.isSubmitting) return;

      const formData = new FormData(this.form);
      const mainProductLine = this.getMainProductLine(formData);
      if (!mainProductLine) return;

      this.isSubmitting = true;
      this.setLoading(true);
      this.productForm?.handleErrorMessage?.();

      const payload = { items: [mainProductLine, ...addonLines] };
      if (this.cart) {
        payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
        payload.sections_url = window.location.pathname;
        this.cart.setActiveElement(document.activeElement);
      }

      try {
        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(payload),
        });
        let responseData;
        if (response.status === 429) {
          responseData = { status: 429 };
        } else if ((response.headers.get('content-type') || '').includes('application/json')) {
          responseData = await response.json();
        } else if (!response.ok) {
          responseData = {
            status: response.status,
            message: response.statusText || 'Unable to add items to cart.',
          };
        } else {
          responseData = await response.json();
        }

        if (!response.ok || responseData.status) {
          const errorMessage =
            response.status === 429 || Number(responseData.status) === 429
              ? 'You\'re adding items too quickly. Please wait a moment and try again.'
              : responseData.errors || responseData.description || responseData.message || 'Unable to add items to cart.';

          throw Object.assign(new Error(errorMessage), {
            responseData,
            status: response.status,
          });
        }

        if (!this.cart) {
          this.decrementRemainingInventory(addonLines);
          window.location = window.routes?.cart_url || '/cart';
          return;
        }

        this.decrementRemainingInventory(addonLines);

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          await publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: mainProductLine.id,
            cartData: responseData,
          });
        }

        this.cart.renderContents(responseData);
        this.productForm?.dispatchEvent(
          new CustomEvent('product-form:success', {
            bubbles: true,
            detail: { productVariantId: mainProductLine.id, cartData: responseData },
          })
        );
      } catch (error) {
        const message =
          error.status === 429 || Number(error.responseData?.status) === 429
            ? 'You\'re adding items too quickly. Please wait a moment and try again.'
            : error.responseData?.errors || error.responseData?.description || error.responseData?.message || error.message;
        this.productForm?.handleErrorMessage?.(message);

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: mainProductLine.id,
            errors: message,
            message,
          });
        }

        if (window.StandardEvents?.CartErrorEvent && this.productForm) {
          this.productForm.dispatchEvent(new window.StandardEvents.CartErrorEvent({ error: message, code: 'INVALID' }));
        }
      } finally {
        this.isSubmitting = false;
        this.setLoading(false);
      }
    }

    setLoading(loading) {
      const submitButton = this.productForm?.querySelector('[type=submit]');
      const spinner = this.productForm?.querySelector('.loading__spinner');
      if (!submitButton) return;

      if (loading) {
        submitButton.setAttribute('aria-disabled', 'true');
      } else {
        submitButton.removeAttribute('aria-disabled');
      }
      submitButton.classList.toggle('loading', loading);
      spinner?.classList.toggle('hidden', !loading);

      if (!loading && this.cart?.classList.contains('is-empty')) {
        this.cart.classList.remove('is-empty');
      }
    }
  }

  if (!customElements.get('yg-product-addons')) {
    customElements.define('yg-product-addons', YGProductAddons);
  }

  class YGLogoList extends HTMLElement {
    connectedCallback() {
      if (this.isInitialized || this.dataset.marquee !== 'true') return;

      this.track = this.querySelector('[data-logo-list-track]');
      this.group = this.querySelector('[data-logo-list-group]');
      if (!this.track || !this.group) return;

      this.isInitialized = true;
      this.clone = this.group.cloneNode(true);
      this.clone.removeAttribute('data-logo-list-group');
      this.clone.setAttribute('aria-hidden', 'true');
      this.track.appendChild(this.clone);

      this.updateMetrics = this.updateMetrics.bind(this);
      this.resizeObserver = new ResizeObserver(this.updateMetrics);
      this.resizeObserver.observe(this.group);
      this.resizeObserver.observe(this);
      window.requestAnimationFrame(this.updateMetrics);
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.clone?.remove();
      this.style.removeProperty('--product-logo-list-distance');
      this.style.removeProperty('--product-logo-list-duration');
      this.isInitialized = false;
    }

    updateMetrics() {
      if (!this.group || !this.track) return;

      const trackStyles = window.getComputedStyle(this.track);
      const trackGap = Number.parseFloat(trackStyles.columnGap || trackStyles.gap) || 0;
      const distance = this.group.getBoundingClientRect().width + trackGap;
      const pixelsPerSecond = 45;

      this.style.setProperty('--product-logo-list-distance', `${distance}px`);
      this.style.setProperty('--product-logo-list-duration', `${Math.max(distance / pixelsPerSecond, 10)}s`);
    }
  }

  if (!customElements.get('yg-logo-list')) {
    customElements.define('yg-logo-list', YGLogoList);
  }

  class YGAccordion extends YGUtils {
    constructor() {
      super();
      this.init();
    }

    init() {
      const header = this.querySelector(".yg-accordion-header");
      const content = this.querySelector(".yg-accordion-content");
      if (!header || !content || this.classList.contains("yg-accordion-disabled")) return;

      const trueHeight = content.scrollHeight;
      content.style.setProperty("--true-height", `${trueHeight}px`);
      header.addEventListener("click", () => {
        this.classList.toggle("yg-accordion-open");
      });
    }
  }

  if (!window.customElements.get("yg-accordion")) {
    window.customElements.define("yg-accordion", YGAccordion);
  }

  function getScrollRestoreKey() {
    return `${scrollRestoreStorageKeyPrefix}${window.location.pathname}${window.location.search}`;
  }

  function saveReturnScrollPosition() {
    try {
      window.sessionStorage.setItem(
        getScrollRestoreKey(),
        JSON.stringify({
          x: window.scrollX || window.pageXOffset || 0,
          y: window.scrollY || window.pageYOffset || 0,
          expires: Date.now() + 30 * 60 * 1000,
        })
      );
    } catch (error) {
      // Continue with native browser restoration if sessionStorage is unavailable.
    }
  }

  function isBackForwardNavigation(event) {
    // bfcache 恢复
    if (event && event.persisted) return true;

    // 现代浏览器：Navigation Timing Level 2
    try {
      const navEntry = window.performance?.getEntriesByType?.("navigation")?.[0];
      if (navEntry && navEntry.type === "back_forward") return true;
    } catch (error) {
      // ignore
    }

    // 旧浏览器兜底：2 === TYPE_BACK_FORWARD
    if (window.performance?.navigation && window.performance.navigation.type === 2) return true;

    return false;
  }

  function consumeSavedScrollPosition() {
    try {
      window.sessionStorage.removeItem(getScrollRestoreKey());
    } catch (error) {
      // ignore
    }
  }

  function restoreReturnScrollPosition(event) {
    if (window.location.hash) return;

    // 仅在浏览器后退/前进（含 bfcache）时恢复，正常打开和刷新使用浏览器原生行为
    if (!isBackForwardNavigation(event)) return;

    let savedPosition = null;

    try {
      savedPosition = JSON.parse(window.sessionStorage.getItem(getScrollRestoreKey()) || "null");
    } catch (error) {
      savedPosition = null;
    }

    // 读取后立即消费，避免后续加载重复触发
    consumeSavedScrollPosition();

    if (!savedPosition || savedPosition.expires < Date.now()) return;

    const scrollToSavedPosition = () => {
      window.scrollTo({
        left: Number(savedPosition.x) || 0,
        top: Number(savedPosition.y) || 0,
        behavior: "instant",
      });
    };

    const timers = [];
    const cancelRestore = () => {
      timers.forEach((id) => window.clearTimeout(id));
      timers.length = 0;
      ["wheel", "touchstart", "keydown", "pointerdown"].forEach((type) =>
        window.removeEventListener(type, cancelRestore, true)
      );
    };

    // 用户一旦主动交互即停止恢复，避免抢夺滚动
    ["wheel", "touchstart", "keydown", "pointerdown"].forEach((type) =>
      window.addEventListener(type, cancelRestore, { capture: true, passive: true, once: true })
    );

    scrollToSavedPosition();
    window.requestAnimationFrame(scrollToSavedPosition);
    // 兜底应对布局位移（图片/字体加载导致高度变化），交互后即取消
    [100, 300].forEach((delay) => timers.push(window.setTimeout(scrollToSavedPosition, delay)));
  }

  function initReturnScrollRestore() {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = isBackForwardNavigation() ? "manual" : "auto";
    }

    let saveScrollFrame = null;
    const saveScrollPositionSoon = () => {
      if (saveScrollFrame !== null) return;

      saveScrollFrame = window.requestAnimationFrame(() => {
        saveScrollFrame = null;
        saveReturnScrollPosition();
      });
    };

    document.addEventListener(
      "click",
      (event) => {
        const link = event.target.closest?.("a[href]");

        if (!link || event.defaultPrevented || event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (link.target && link.target !== "_self") return;
        if (link.hasAttribute("download")) return;

        let linkUrl;

        try {
          linkUrl = new URL(link.href, window.location.href);
        } catch (error) {
          return;
        }

        if (linkUrl.origin !== window.location.origin) return;
        if (linkUrl.pathname === window.location.pathname && linkUrl.search === window.location.search) return;

        saveReturnScrollPosition();
      },
      true
    );

    window.addEventListener("scroll", saveScrollPositionSoon, { passive: true });
    window.addEventListener("beforeunload", saveReturnScrollPosition);
    window.addEventListener("pagehide", saveReturnScrollPosition);
    // pageshow 已覆盖首次加载与 bfcache 恢复，无需额外裸调用
    window.addEventListener("pageshow", restoreReturnScrollPosition);
  }

  // initReturnScrollRestore();

  function initAnimation(el) {
    // 防止重复初始化
    if (el.dataset.animatedInit) return;

    el.dataset.animatedInit = "true";
    el.classList.add("fade-in-on-scroll");

    if (isShopifyDesignMode) {
      el.classList.add("yg-animated");
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("yg-animated");
          observer.unobserve(entry.target);
        }
      });
    }, options);

    observer.observe(el);

    // 保存 observer，方便销毁
    el._animationObserver = observer;
  }

  class DynamicItem extends HTMLElement {
    connectedCallback() {
      initAnimation(this);
    }

    disconnectedCallback() {
      this._animationObserver?.disconnect();
    }
  }

  // 支持 <yg-animation-item>
  if (!customElements.get("yg-animation-item")) {
    customElements.define("yg-animation-item", DynamicItem);
  }

  function initAnimationItems(root = document) {
    if (!root.querySelectorAll) return;

    if (root.matches?.(animationItemSelector)) {
      initAnimation(root);
    }

    root.querySelectorAll(animationItemSelector).forEach(initAnimation);
  }

  // 支持 class="yg-animation-item"
  initAnimationItems();

  if (isShopifyDesignMode) {
    document.addEventListener("shopify:section:load", (event) => {
      initAnimationItems(event.target);
    });

    const animationMutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            initAnimationItems(node);
          }
        });

        if (
          mutation.type === "attributes" &&
          mutation.target.nodeType === Node.ELEMENT_NODE &&
          mutation.target.matches?.(animationItemSelector)
        ) {
          initAnimation(mutation.target);
        }
      });
    });

    animationMutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class"],
    });
  }

  class YGAnchor extends HTMLElement {
    constructor() {
      super();
      this.observer = null;
      this.sectionToItem = new Map();
      this.sections = [];
      this.items = [];
      this.isScroll = false;
      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleIntersect = this.handleIntersect.bind(this);
    }

    connectedCallback() {
      this.refreshTargets();

      this.addEventListener("click", this.handleClick);
      this.addEventListener("keydown", this.handleKeydown);
      this.setupObserver();
    }

    disconnectedCallback() {
      this.removeEventListener("click", this.handleClick);
      this.removeEventListener("keydown", this.handleKeydown);
      this.disconnectObserver();
    }

    refreshTargets() {
      this.sections = Array.from(document.querySelectorAll("#MainContent .shopify-section"));
      this.items = Array.from(this.querySelectorAll("yg-anchor-item"));
    }

    setupObserver() {
      this.refreshTargets();
      this.disconnectObserver();
      if (!this.sections.length || !this.items.length) return;

      this.observer = new IntersectionObserver(this.handleIntersect, {
        root: null,
        threshold: 0.01,
        rootMargin: "-20% 0px -70% 0px",
      });

      this.items.forEach((item) => {
        const section = this.getSectionFromItem(item);
        if (!section) return;

        this.sectionToItem.set(section, item);
        this.observer.observe(section);
      });
    }

    disconnectObserver() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      this.sectionToItem.clear();
    }

    handleIntersect(entries) {
      if (this.isScroll) return;

      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        const item = this.sectionToItem.get(entry.target);
        if (item) {
          this.setActiveItem(item);
        }
      });
    }

    handleClick(event) {
      const item = event.target.closest("yg-anchor-item");
      if (!item || !this.contains(item)) return;

      this.scrollToItem(item);
    }

    handleKeydown(event) {
      if (event.key !== "Enter" && event.key !== " ") return;

      const item = event.target.closest("yg-anchor-item");
      if (!item || !this.contains(item)) return;

      event.preventDefault();
      this.scrollToItem(item);
    }

    scrollToItem(item) {
      this.refreshTargets();
      const sectionNumber = Number(item.getAttribute("data-section-number") || 0);
      this.isScroll = true;
      this.setActiveItem(item);

      if (sectionNumber === 0) {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      } else {
        const section = this.getSectionFromItem(item);
        if (!section) {
          this.isScroll = false;
          return;
        }

        const offsetTop = section.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({
          top: offsetTop,
          behavior: "smooth",
        });
      }

      window.setTimeout(() => {
        this.isScroll = false;
      }, 600);
    }

    setActiveItem(activeItem) {
      this.items = Array.from(this.querySelectorAll("yg-anchor-item"));
      const activeSectionNumber = activeItem.getAttribute("data-section-number");

      this.items.forEach((item) => {
        const isActive = item.getAttribute("data-section-number") === activeSectionNumber;
        item.classList.toggle("active", isActive);
        item.setAttribute("aria-current", item === activeItem ? "true" : "false");
      });

      this.scrollActiveItemIntoView(activeItem);

      document.dispatchEvent(
        new CustomEvent("YG:anchor-change", {
          bubbles: true,
        })
      );
    }

    scrollActiveItemIntoView(item) {
      if (!this.isScrollable(this)) return;

      const containerRect = this.getBoundingClientRect();
      const targetRect = item.getBoundingClientRect();
      const scrollLeft = targetRect.left - containerRect.left + this.scrollLeft - 260;

      this.scrollTo({
        left: scrollLeft,
        behavior: "smooth",
      });
    }

    getSectionFromItem(item) {
      const sectionNumber = Number(item.getAttribute("data-section-number") || 0);
      if (!Number.isFinite(sectionNumber) || sectionNumber <= 0) return null;

      const sectionIndex = Math.min(sectionNumber - 1, this.sections.length - 1);
      return this.sections[sectionIndex] || null;
    }

    isScrollable(el) {
      if (!el) return false;

      const style = window.getComputedStyle(el);
      const canScrollVertically =
        (style.overflowY === "scroll" || style.overflowY === "auto") && el.scrollHeight > el.clientHeight;
      const canScrollHorizontally =
        (style.overflowX === "scroll" || style.overflowX === "auto") && el.scrollWidth > el.clientWidth;

      return canScrollVertically || canScrollHorizontally;
    }
  }

  if (!customElements.get("yg-anchor")) {
    customElements.define("yg-anchor", YGAnchor);
  }

  // class YGHomeScreenScroll {
  //   constructor() {
  //     this.main = document.querySelector("#MainContent");
  //     this.sections = [];
  //     this.currentIndex = 0;
  //     this.isAnimating = false;
  //     this.touchStartY = 0;
  //     this.touchStartX = 0;
  //     this.touchStartedAt = 0;
  //     this.hasHandledTouch = false;
  //     this.animationTimeout = null;
  //     this.resizeRaf = null;
  //     this.interactiveSectionSelector = [
  //       "yg-media-cut",
  //       ".yg-media-cut",
  //       ".swiper",
  //       "yg-swiper",
  //       "[data-yg-home-scroll-ignore]",
  //       "[data-home-scroll-ignore]"
  //     ].join(",");
  //     this.handleWheel = this.handleWheel.bind(this);
  //     this.handleKeydown = this.handleKeydown.bind(this);
  //     this.handleTouchStart = this.handleTouchStart.bind(this);
  //     this.handleTouchMove = this.handleTouchMove.bind(this);
  //     this.handleTouchEnd = this.handleTouchEnd.bind(this);
  //     this.handleResize = this.handleResize.bind(this);
  //   }

  //   init() {
  //     if (!this.shouldEnable()) return;

  //     this.refreshSections();
  //     if (this.sections.length < 2) return;

  //     document.documentElement.classList.add("yg-home-screen-scroll");
  //     this.sections.forEach((section) => {
  //       section.classList.add("yg-home-screen-scroll__section");
  //     });

  //     window.addEventListener("wheel", this.handleWheel, { passive: false });
  //     window.addEventListener("keydown", this.handleKeydown);
  //     document.addEventListener("touchstart", this.handleTouchStart, { passive: true });
  //     document.addEventListener("touchmove", this.handleTouchMove, { passive: false });
  //     document.addEventListener("touchend", this.handleTouchEnd, { passive: false });
  //     window.addEventListener("resize", this.handleResize);

  //     this.currentIndex = this.getNearestSectionIndex();
  //   }

  //   shouldEnable() {
  //     if (!this.main || window.Shopify?.designMode) return false;
  //     if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;

  //     const rootPath = window.Shopify?.routes?.root || "/";
  //     const normalizedRoot = this.normalizePath(rootPath);
  //     const normalizedPath = this.normalizePath(window.location.pathname);

  //     return normalizedPath === normalizedRoot;
  //   }

  //   normalizePath(path) {
  //     const normalized = `/${String(path || "/").replace(/^\/+|\/+$/g, "")}`;
  //     return normalized === "/" ? "/" : normalized.toLowerCase();
  //   }

  //   refreshSections() {
  //     this.sections = Array.from(this.main.querySelectorAll(":scope > .shopify-section")).filter((section) => {
  //       const style = window.getComputedStyle(section);
  //       return style.display !== "none" && style.visibility !== "hidden" && section.offsetHeight > 0;
  //     });

  //     this.currentIndex = this.clamp(this.currentIndex, 0, Math.max(this.sections.length - 1, 0));
  //   }

  //   handleWheel(event) {
  //     if (!this.canHijackEvent(event)) return;

  //     const direction = Math.sign(event.deltaY);
  //     if (direction === 0) return;
  //     if (!this.canMoveByDirection(direction)) return;

  //     event.preventDefault();
  //     this.goToIndex(this.currentIndex + direction);
  //   }

  //   handleKeydown(event) {
  //     if (!this.canHijackEvent(event)) return;

  //     const nextKeys = ["ArrowDown", "PageDown", "Space"];
  //     const previousKeys = ["ArrowUp", "PageUp"];
  //     const homeKeys = ["Home"];
  //     const endKeys = ["End"];

  //     if (nextKeys.includes(event.code)) {
  //       if (!this.canMoveByDirection(1)) return;
  //       event.preventDefault();
  //       this.goToIndex(this.currentIndex + 1);
  //     } else if (previousKeys.includes(event.code)) {
  //       if (!this.canMoveByDirection(-1)) return;
  //       event.preventDefault();
  //       this.goToIndex(this.currentIndex - 1);
  //     } else if (homeKeys.includes(event.code)) {
  //       event.preventDefault();
  //       this.goToIndex(0);
  //     } else if (endKeys.includes(event.code)) {
  //       event.preventDefault();
  //       this.goToIndex(this.sections.length - 1);
  //     }
  //   }

  //   handleTouchStart(event) {
  //     if (!event.touches || event.touches.length !== 1) return;

  //     this.touchStartY = event.touches[0].clientY;
  //     this.touchStartX = event.touches[0].clientX;
  //     this.touchStartedAt = Date.now();
  //     this.hasHandledTouch = false;
  //     this.currentIndex = this.getNearestSectionIndex();
  //   }

  //   handleTouchMove(event) {
  //     if (this.hasHandledTouch) {
  //       event.preventDefault();
  //       return;
  //     }

  //     if (!this.canHijackEvent(event) || !event.touches || event.touches.length !== 1) return;

  //     const touch = event.touches[0];
  //     const deltaY = this.touchStartY - touch.clientY;
  //     const deltaX = this.touchStartX - touch.clientX;

  //     if (Math.abs(deltaY) < 42 || Math.abs(deltaY) < Math.abs(deltaX)) return;

  //     const direction = Math.sign(deltaY);
  //     if (!this.canMoveByDirection(direction)) return;

  //     event.preventDefault();
  //     this.hasHandledTouch = true;
  //     this.goToIndex(this.currentIndex + direction);
  //   }

  //   handleTouchEnd(event) {
  //     if (this.hasHandledTouch) {
  //       event.preventDefault();
  //       this.hasHandledTouch = false;
  //       return;
  //     }

  //     if (!this.canHijackEvent(event) || !event.changedTouches || event.changedTouches.length !== 1) return;

  //     const touch = event.changedTouches[0];
  //     const deltaY = this.touchStartY - touch.clientY;
  //     const deltaX = this.touchStartX - touch.clientX;
  //     const elapsed = Date.now() - this.touchStartedAt;

  //     if (Math.abs(deltaY) < 48 || Math.abs(deltaY) < Math.abs(deltaX) || elapsed > 900) return;

  //     const direction = Math.sign(deltaY);
  //     if (!this.canMoveByDirection(direction)) return;

  //     event.preventDefault();
  //     this.goToIndex(this.currentIndex + direction);
  //   }

  //   handleResize() {
  //     if (this.resizeRaf) return;

  //     this.resizeRaf = window.requestAnimationFrame(() => {
  //       this.resizeRaf = null;
  //       this.refreshSections();
  //       this.currentIndex = this.getNearestSectionIndex();
  //     });
  //   }

  //   canHijackEvent(event) {
  //     if (this.isAnimating) return false;
  //     if (event.defaultPrevented) return false;
  //     if (event.ctrlKey || event.metaKey || event.altKey) return false;
  //     if (event.target?.closest?.("input, textarea, select, option, [contenteditable='true'], modal-dialog, details-modal, .shopify-policy__container")) return false;
  //     if (this.hasScrollableParent(event.target)) return false;

  //     return true;
  //   }

  //   canMoveByDirection(direction) {
  //     this.refreshSections();

  //     const currentSection = this.sections[this.currentIndex] || this.sections[this.getNearestSectionIndex()];
  //     const targetIndex = this.clamp(this.currentIndex + direction, 0, this.sections.length - 1);
  //     const targetSection = this.sections[targetIndex];

  //     if (!currentSection || !targetSection) return false;
  //     if (targetIndex === this.currentIndex) return false;
  //     if (this.isInteractiveSection(currentSection)) return false;

  //     return true;
  //   }

  //   isInteractiveSection(section) {
  //     if (!section) return false;
  //     if (section.matches("[data-yg-home-scroll-ignore], [data-home-scroll-ignore]")) return true;
  //     if (section.querySelector(this.interactiveSectionSelector)) return true;

  //     const stickyElement = section.querySelector("[style*='sticky'], .sticky, [class*='sticky']");
  //     if (stickyElement) return true;

  //     return this.hasInternalScrollableElement(section);
  //   }

  //   hasInternalScrollableElement(section) {
  //     const elements = Array.from(section.querySelectorAll("*"));

  //     return elements.some((element) => {
  //       if (element === section) return false;

  //       const style = window.getComputedStyle(element);
  //       const canScrollY = /(auto|scroll)/.test(style.overflowY) && element.scrollHeight > element.clientHeight + 1;
  //       const canScrollX = /(auto|scroll)/.test(style.overflowX) && element.scrollWidth > element.clientWidth + 1;

  //       return canScrollY || canScrollX;
  //     });
  //   }

  //   hasScrollableParent(target) {
  //     let element = target instanceof Element ? target : null;

  //     while (element && element !== document.body && element !== this.main) {
  //       const style = window.getComputedStyle(element);
  //       const canScrollY = /(auto|scroll)/.test(style.overflowY) && element.scrollHeight > element.clientHeight + 1;

  //       if (canScrollY) {
  //         const atTop = element.scrollTop <= 0;
  //         const atBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 1;

  //         if (!atTop || !atBottom) return true;
  //       }

  //       element = element.parentElement;
  //     }

  //     return false;
  //   }

  //   goToIndex(index) {
  //     this.refreshSections();
  //     if (!this.sections.length) return;

  //     const targetIndex = this.clamp(index, 0, this.sections.length - 1);
  //     if (targetIndex === this.currentIndex && Math.abs(window.scrollY - this.getSectionTop(this.sections[targetIndex])) < 2) {
  //       return;
  //     }

  //     const targetSection = this.sections[targetIndex];
  //     if (!targetSection) return;

  //     this.currentIndex = targetIndex;
  //     this.isAnimating = true;

  //     window.scrollTo({
  //       top: this.getSectionTop(targetSection),
  //       behavior: "smooth",
  //     });

  //     window.clearTimeout(this.animationTimeout);
  //     this.animationTimeout = window.setTimeout(() => {
  //       this.isAnimating = false;
  //       this.currentIndex = this.getNearestSectionIndex();
  //     }, 850);
  //   }

  //   getNearestSectionIndex() {
  //     if (!this.sections.length) return 0;

  //     const viewportMiddle = window.scrollY + window.innerHeight / 2;
  //     let closestIndex = 0;
  //     let closestDistance = Number.POSITIVE_INFINITY;

  //     this.sections.forEach((section, index) => {
  //       const rect = section.getBoundingClientRect();
  //       const sectionMiddle = window.scrollY + rect.top + rect.height / 2;
  //       const distance = Math.abs(sectionMiddle - viewportMiddle);

  //       if (distance < closestDistance) {
  //         closestDistance = distance;
  //         closestIndex = index;
  //       }
  //     });

  //     return closestIndex;
  //   }

  //   getSectionTop(section) {
  //     return Math.round(section.getBoundingClientRect().top + window.pageYOffset);
  //   }

  //   clamp(value, min, max) {
  //     return Math.min(Math.max(value, min), max);
  //   }
  // }

  // new YGHomeScreenScroll().init();
});
